import {
  createDropdown,
  addListToDropdown,
  Plugin,
  ViewModel,
  Collection
} from 'ckeditor5';
import TemplateStore from 'src/store/TemplateStore';
import { recommendTemplates } from './recommendTemplates';

// How many recommended templates to surface at most, so the section stays a
// quick shortcut rather than a second full list.
const MAX_RECOMMENDED = 5;

const templateIcon = `
 <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <!-- First Rectangle -->
  <path clip-rule="evenodd" d="M8 0H3a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h5a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2ZM2.5 2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 .5.5v15a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V2Z" fill="black"/>

  <!-- Second Rectangle -->
  <path clip-rule="evenodd" d="M13 0h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2Zm0 1.5a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h5a.5.5 0 0 0 .5-.5V2a.5.5 0 0 0-.5-.5h-5Z" fill="black"/>

  <!-- Third Rectangle -->
  <path clip-rule="evenodd" d="M13 10h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2Zm0 1.5a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h5a.5.5 0 0 0 .5-.5v-5a.5.5 0 0 0-.5-.5h-5Z" fill="black"/>
</svg>
`;
export default class TemplateDropdown extends Plugin {
  static get pluginName() {
    return 'TemplateDropdown';
  }

  async init() {
    const editor = this.editor;
    const t = editor.t;
    const dropdownTooltip = t('Insert Template');

    const store = TemplateStore.getInstance();
    const templates = await store.getTemplates();

    editor.ui.componentFactory.add('templateDropdown', (locale) => {
      const dropdownView = createDropdown(locale);

      dropdownView.buttonView.set({
        label: 'Template',
        tooltip: dropdownTooltip || 'Insert a template',
        icon: templateIcon,
        withText: true
      });

      dropdownView.extendTemplate({
        attributes: {
          class: ['ck-template-dropdown', 'tgc-tb-brand']
        }
      });

      const templateButton = (template, tooltip) => {
        const model = {
          id: template._id,
          label: template.name,
          content: template.content,
          version: template.version,
          withText: true
        };
        if (tooltip) model.tooltip = tooltip;
        return { type: 'button', model: new ViewModel(model) };
      };

      const templateGroup = (label, list, tooltipFor) => {
        const groupItems = new Collection();
        list.forEach((template) => {
          groupItems.add(
            templateButton(template, tooltipFor && tooltipFor(template))
          );
        });
        return { type: 'group', label, items: groupItems };
      };

      // The dropdown list is bound to this collection, so mutating it after the
      // dropdown has been opened re-renders the menu. We rebuild it on every
      // open to reflect the latest Contact Type / Mode / Status.
      const definitions = new Collection();

      const rebuild = () => {
        definitions.clear();

        if (!templates.length) {
          definitions.add({
            type: 'button',
            model: new ViewModel({
              label: t('No templates available'),
              withText: true,
              isEnabled: false
            })
          });
          return;
        }

        const context = editor._lmContactContext || {};
        const { recommended, others } = recommendTemplates(templates, context, {
          max: MAX_RECOMMENDED
        });

        if (recommended.length) {
          // const reasonFor = (template) => {
          //   const hit = recommended.find((r) => r.template === template);
          //   const fields =
          //     hit && hit.reasons.length
          //       ? hit.reasons.map((r) => r.label).join(', ')
          //       : '';
          //   return fields ? `Recommended — matches ${fields}` : 'Recommended';
          // };

          definitions.add(
            templateGroup(
              'Suggested templates',
              recommended.map((r) => r.template)
              // reasonFor
            )
          );
        }

        // Always show the "All templates" heading, even when nothing is
        // recommended, so the list header stays consistent.
        definitions.add(templateGroup('All templates', others));
      };

      rebuild();
      addListToDropdown(dropdownView, definitions);

      dropdownView.on('change:isOpen', (evt, name, isOpen) => {
        if (isOpen) rebuild();
      });

      dropdownView.on('execute', (evt) => {
        const templateData = evt.source.content;
        if (templateData == null) return;

        editor._lmTemplateMeta = {
          templateId: evt.source.id,
          templateVersion: evt.source.version,
          templateName: evt.source.label
        };

        editor.model.change(() => {
          const viewFragment = editor.data.processor.toView(templateData);
          const modelFragment = editor.data.toModel(viewFragment);

          const selection = editor.model.document.selection;
          editor.model.insertContent(modelFragment, selection);
        });
      });

      return dropdownView;
    });
  }
}
